import { useCookies } from 'next-client-cookies';
import { getLocalizationInfo } from 'project.config';

export const useResolvedLocalizedObject = (localized: object | string) => {

  const cookies = useCookies();
  const nextLocale = cookies.get('locale') as string;

  const { locale } = getLocalizationInfo(nextLocale);

  if (typeof localized === 'string') return localized;

  return localized[locale as keyof typeof localized] ?? Object.values(localized)[0] ?? '';
};

export const useLocalizedObjectWithoutDefault = (localized: object | string) => {
  const cookies = useCookies();
  const nextLocale = cookies.get('locale') as string;

  const { locale } = getLocalizationInfo(nextLocale);

  if (typeof localized === 'string') return localized;

  return localized[locale as keyof typeof localized] ?? '';
};

//this is similar function as the above hook where we can pass locale from our Frontend Component
export const resolvedLocalizedObject = (localized: object | string, nextLocale: string) => {
  const { locale } = getLocalizationInfo(nextLocale);

  if (typeof localized === 'string') return localized;

  return localized[locale as keyof typeof localized] ?? Object.values(localized)[0] ?? '';
};

export const normalizeLocale = (locale: any): string => {
  if (!locale) return '';

  // Convert to lowercase and split by '-'
  const parts = locale.toLowerCase().split('-');

  if (parts.length === 2) {
    // First part stays lowercase, second part becomes uppercase
    return `${parts[0]}_${parts[1].toUpperCase()}`;
  }

  return locale; // Return as-is if no hyphen
};

type LocalizedMap = Record<string, string>;
type Localizable = string | LocalizedMap;

const normalizeLocaleKey = (
  locale: string | null | undefined
): string => {
  if (!locale) return '';

  const parts = locale.toLowerCase().split('-');

  if (parts.length === 2) {
    return `${parts[0]}_${parts[1].toUpperCase()}`;
  }

  return parts[0];
};

export const resolveLocalizable = (value: Localizable, locale: string | undefined): string => {
  if (typeof value === 'string') return value;

  const key = normalizeLocaleKey(locale);
  if (value[key]) {
    return value[key];
  }
  const langOnly = key.split('_')[0];
  const matchingKey = Object.keys(value)?.find((k) => k.toLowerCase().startsWith(`${langOnly.toLowerCase()}_`));
  if (matchingKey) {
    return value[matchingKey];
  }
  return Object.values(value)[0] ?? '';
};

const isLocalizedMap = (v: unknown): v is LocalizedMap =>
  !!v && typeof v === 'object' && !Array.isArray(v) && Object.values(v as Record<string, unknown>).every(x => typeof x === 'string');

export const resolveValueDeep = (v: unknown, locale: string | undefined): any => {
  if (v == null) return v;

  if (typeof v === 'string') return v;

  if (Array.isArray(v)) return v.map(item => resolveValueDeep(item, locale));

  if (isLocalizedMap(v)) return resolveLocalizable(v, locale);

  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(obj)) {
      out[k] = resolveValueDeep(val, locale);
    }
    return out;
  }

  return v;
};

export const resolveTextConfiguration = <T extends Record<string, any>>(
  texts: T,
  locale: string | undefined
): T => {
  return resolveValueDeep(texts, locale) as T;
};
